import { useRef, useState } from "react";
import { FormField } from "../components/FormField.js";
import { SuccessState, ValidationMessage } from "../components/States.js";
import { required, isEmail } from "../shared/validate.js";
import { apiPost } from "../client/apiClient.js";
import { trackEvent } from "../client/analytics.js";
import type { ApiResult } from "../shared/types.js";

interface ContactDraft {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  privacyConsent: boolean;
}

export function ContactForm() {
  const [draft, setDraft] = useState<ContactDraft>({ name: "", email: "", phone: "", subject: "", message: "", privacyConsent: false });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; error?: string } | null>(null);
  const renderedAt = useRef(Date.now());

  function patch(p: Partial<ContactDraft>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const set = (field: string, r: { valid: boolean; message?: string }) => {
      if (!r.valid) errors[field] = r.message!;
    };
    set("name", required(draft.name, "Naam"));
    set("email", isEmail(draft.email));
    set("subject", required(draft.subject, "Onderwerp"));
    set("message", required(draft.message, "Bericht"));
    if (!draft.privacyConsent) errors.privacyConsent = "Ga akkoord met de privacyverklaring om verder te gaan.";
    return errors;
  }

  const errors = validate();
  const err = (f: string) => (touched[f] ? errors[f] : undefined);

  async function submit(e: any) {
    e.preventDefault();
    setTouched({ name: true, email: true, subject: true, message: true, privacyConsent: true });
    if (Object.keys(errors).length > 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = (await apiPost("/api/contact", {
        ...draft,
        honeypot: (document.getElementById("contact-website") as HTMLInputElement | null)?.value || "",
        formRenderedAt: renderedAt.current,
      })) as ApiResult<null>;
      if (res.ok) trackEvent("contact_form_submitted");
      setResult(res.ok ? { ok: true } : { ok: false, error: res.error });
    } catch {
      setResult({ ok: false, error: "Er ging iets mis. Probeer het later opnieuw." });
    } finally {
      setSubmitting(false);
    }
  }

  if (result?.ok) {
    return (
      <SuccessState title="Bedankt voor uw bericht!">
        <p>We nemen zo snel mogelijk contact met u op.</p>
      </SuccessState>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <FormField id="name" label="Naam" required value={draft.name} onChange={(v) => patch({ name: v })} onBlur={() => setTouched((t) => ({ ...t, name: true }))} error={err("name")} autoComplete="name" />
      <FormField id="email" label="E-mail" type="email" required value={draft.email} onChange={(v) => patch({ email: v })} onBlur={() => setTouched((t) => ({ ...t, email: true }))} error={err("email")} autoComplete="email" />
      <FormField id="phone" label="Telefoon (optioneel)" type="tel" value={draft.phone} onChange={(v) => patch({ phone: v })} autoComplete="tel" inputMode="tel" />
      <FormField id="subject" label="Onderwerp" required value={draft.subject} onChange={(v) => patch({ subject: v })} onBlur={() => setTouched((t) => ({ ...t, subject: true }))} error={err("subject")} />
      <FormField id="message" label="Bericht" as="textarea" rows={5} required value={draft.message} onChange={(v) => patch({ message: v })} onBlur={() => setTouched((t) => ({ ...t, message: true }))} error={err("message")} />

      <div className="checkbox-field">
        <input type="checkbox" id="privacyConsent" checked={draft.privacyConsent} onChange={(e: any) => patch({ privacyConsent: e.target.checked })} />
        <label htmlFor="privacyConsent">
          Ik ga akkoord met de verwerking van mijn gegevens volgens de <a href="/privacybeleid">privacyverklaring</a>. *
        </label>
      </div>
      <ValidationMessage>{touched.privacyConsent ? errors.privacyConsent : undefined}</ValidationMessage>

      <div className="hp-field" aria-hidden="true">
        <label htmlFor="contact-website">Website (niet invullen)</label>
        <input type="text" id="contact-website" name="contact-website" tabIndex={-1} autoComplete="off" />
      </div>

      {result && !result.ok && <ValidationMessage>{result.error}</ValidationMessage>}

      <button type="submit" className="btn btn-primary btn-lg mt-4" disabled={submitting}>
        {submitting ? "Bezig met verzenden…" : "Verstuur bericht"}
      </button>
    </form>
  );
}
